import { parse, stringify } from "query-string";
import React from "react";
import { totalPrize } from "./payouts";

export const CheapTournamentEntryFee = 8;

var entryFilters = [
  {
    key: "all",
    name: "All Entry Fee Amounts",
    group: "entry"
  },
  {
    key: "points",
    name: (
      <span>
        <i className="dw-points-solid" />

        {" "}

        Points
      </span>
    ),
    fn: (tournament) => tournament.get("points_buy_in_amount"),
    group: "entry"
  },
  {
    key: "undersmall",
    name: "$1 - $" + CheapTournamentEntryFee,
    fn: (tournament) => parseFloat(tournament.get("money_buy_in_amount")) <= CheapTournamentEntryFee,
    group: "entry"
  },
  {
    key: "smallto20",
    name: "$" + (CheapTournamentEntryFee + 1) + " - $20",
    fn: (tournament) => {
      var actual = parseFloat(tournament.get("money_buy_in_amount"));
      return actual >= (CheapTournamentEntryFee + 1) && actual <= 20;
    },
    group: "entry"
  },
  {
    key: "20to40",
    name: "$20 - $40",
    fn: (tournament) => {
      var actual = parseFloat(tournament.get("money_buy_in_amount"));
      return actual >= 20 && actual <= 40;
    },
    group: "entry"
  },
  {
    key: "40to100",
    name: "$40 - $100",
    fn: (tournament) => {
      var actual = parseFloat(tournament.get("money_buy_in_amount"));
      return actual >= 40 && actual <= 100;
    },
    group: "entry"
  },
  {
    key: "100to250",
    name: "$100 - $250",
    fn: (tournament) => {
      var actual = parseFloat(tournament.get("money_buy_in_amount"));
      return actual >= 100 && actual <= 250;
    },
    group: "entry"
  },
  {
    key: "250andup",
    name: "$250 and Up",
    fn: (tournament) => {
      return parseFloat(tournament.get("money_buy_in_amount")) >= 250;
    },
    group: "entry"
  }
];

var gameTypeFilters = [
  {
    key: "all",
    name: "All Game Types",
    group: "game-type"
  },
  {
    badge: "featured",
    key: "featured",
    name: "Featured Game",
    fn: (tournament) => tournament.get("featured"),
    group: "game-type"
  },
  {
    badge: "guaranteed",
    key: "guaranteed",
    name: "Guaranteed Game",
    fn: (tournament) => tournament.get("number_of_users_required") <= 1,
    group: "game-type"
  },
  {
    badge: "lockdown",
    key: "lockdown",
    name: "Lockdown Game",
    fn: (tournament) => tournament.get("lockdown"),
    group: "game-type"
  },
  {
    badge: "qualifier",
    key: "qualifier",
    name: "Qualifier Game",
    fn: (tournament) => tournament.get("qualifier") || tournament.get("parent_id"),
    group: "game-type"
  }
];

var prizeFilters = [
  {
    key: "all",
    name: "All Prize Amounts",
    group: "prize"
  },
  {
    key: "10kandup",
    name: "$10,000 and Up",
    fn: (tournament, tokenTypes) => totalPrize(tournament, tokenTypes) >= 10000,
    group: "prize"
  },
  {
    key: "1kto10k",
    name: "$1,000 to $10,000",
    fn: (tournament, tokenTypes) => {
      var actual = totalPrize(tournament, tokenTypes);
      return actual >= 1000 && actual <= 10000;
    },
    group: "prize"
  },
  {
    key: "100to1k",
    name: "$100 to $1,000",
    fn: (tournament, tokenTypes) => {
      var actual = totalPrize(tournament, tokenTypes);
      return actual >= 100 && actual <= 1000;
    },
    group: "prize"
  },
  {
    key: "under100",
    name: "$100 and Under",
    fn: (tournament, tokenTypes) => totalPrize(tournament, tokenTypes) <= 100,
    group: "prize"
  }
];

var filterGroups = [
  {
    name: "Status",
    key: "status",
    onlyShowOnTag: true,
    filters: [
      {
        key: "all",
        name: "Upcoming",
        fn: (tournament) => tournament.get("status") === "scheduled" || tournament.get("status") === "open",
        group: "status"
      },
      {
        key: "started",
        name: "Started",
        alwaysShow: true,
        fn: (tournament) => tournament.get("status") === "final",
        group: "status"
      }
    ]
  },
  {
    name: "Game Type",
    key: "game-type",
    filters: gameTypeFilters,
    summary: {
      plural: true,
      suffix: "Game"
    }
  },
  {
    name: "Track",
    key: "track",
    expand: (tournaments, races) => {
      var tracks = tournaments
        .flatMap(t => t.get("races"))
        .map(raceId => races.get(raceId))
        .reduce((ts, race) => {
          ts[race.get("track_abbreviation")] = race.get("track_name");
          return ts;
        }, {});

      return [
        {
          key: "all",
          name: "All Tracks",
          group: "track"
        }
      ].concat(
        Object.keys(tracks)
          .map(track => ({
            key: track,
            name: tracks[track],
            fn: tournament => tournament.get("races").map(raceId => races.get(raceId).get("track_abbreviation")).includes(track),
            group: "track"
          }))
      );
    },
    filter: (tracks, tournament, tokenTypes, races) => {
      if (tracks.indexOf("all") !== -1) {
        return true;
      }
      var abbrs = tournament.get("races").map(raceId => races.get(raceId).get("track_abbreviation"));
      var match = abbrs.find(abbr => tracks.indexOf(abbr) !== -1);
      return match !== undefined;
    },
    summary: {
      prefix: "at"
    }
  },
  {
    name: "Entry Fee Amount",
    key: "entry",
    filters: entryFilters,
    summary: {
      plural: true
    }
  },
  {
    name: "Prize Amount",
    key: "prize",
    filters: prizeFilters,
    summary: {
      plural: true
    }
  },
  {
    name: "Token",
    key: "tokenType",
    filter: (tokenTypes, tournament) => {
      if (tokenTypes.indexOf("all") !== -1) {
        return true;
      }
      return tournament.get("token_type_id") === parseInt(tokenTypes[0]);
    }
  }
];

export function getFilterGroups(tournaments, races, tag) {
  return filterGroups
    .filter(group => !group.onlyShowOnTag || tag)
    .map(group => expandGroup(group, tournaments, races));
}

function expandGroup(group, tournaments, races) {
  if (!group.expand) {
    return group;
  }

  return {
    name: group.name,
    key: group.key,
    filter: group.filter,
    filters: group.expand(tournaments, races),
    summary: group.summary
  };
}

export function isActive({ key, group }, location) {
  var q = parse(location.search);
  var filters = normalizeFilters(q[group]);

  if (key !== "all") {
    return filters.indexOf(key) !== -1;
  } else {
    return filters.length === 0;
  }
}

function normalizeFilters(filters) {
  if (!filters) {
    return [];
  }
  if (typeof filters === "string") {
    return [filters];
  }
  return filters;
}

export function getFilterQuery({ key, group }, location) {
  var q = parse(location.search);
  var filters = normalizeFilters(q[group]);

  if (key !== "all") {
    q[group] = toggleFilter(filters, key);
  } else {
    delete q[group];
  }

  return stringify(q);
}

function toggleFilter(filters, filter) {
  if (filters.indexOf(filter) === -1) {
    return filters.concat(filter);
  } else {
    return filters.filter(f => f !== filter);
  }
}
